/**
 * CalendarSync.ts
 *
 * 캘린더 및 이벤트 동기화를 담당하는 모듈
 *
 * 주요 기능:
 * - 캘린더 목록 동기화 (fetchCalendars)
 * - 이벤트 데이터 동기화 (fetchEvents)
 * - 동기화 큐 관리 및 최적화
 * - 캘린더별 동기화 설정 관리
 * - 이벤트 데이터 파싱 및 처리
 *
 * 이 모듈은 CalDAV 서버와의 데이터 동기화를 효율적으로 관리하여
 * 로컬 상태와 서버 상태를 일치시킵니다.
 */

import { DAVNamespaceShort, DAVCalendar, DAVCalendarObject } from 'libs/tsdav'
import { parseICS, VEvent, CalendarResponse } from 'node-ical'

import settings from 'popups/Settings'
import { basicColors } from 'utils/ui'
import * as dateUtils from '../utils/DateUtils'
import { refineEvent } from './RefineEvent'
import { stringToHash } from 'utils/ui'
// 순환 참조 방지를 위해 EventManager 대신 공유 유틸리티에서 import
import { isPersonaEmail, getDisplayEmailFromPersonaEmail } from 'utils/personaEmail'
// TodoService barrel re-export 순환 참조 방지를 위해 dynamic import 사용

import { getAppServices } from 'env'
import settingSyncService from 'services/settingSync'

import {
  classInfo,
  userInfo,
  currentDate,
  folderEmitter,
  instanceEmitter,
  _emitSync,
  isFetchingCalendars,
  fetchingEventQueue,
  latestTimeRange,
  setIsFetchingCalendars,
  setFetchingEventQueue,
  setLatestTimeRange,
} from './CalendarState'
import { getClient } from './CalendarClient'
import { _removeInstances, _createInstances } from './EventInstanceManager'
import { fetchNotifications } from './NotificationService'

/**
 * Extended calendar interface with application-specific properties
 */
export interface AppCalendar {
  url: string
  displayName?: string | Record<string, unknown>
  description?: string
  ctag?: string
  syncToken?: string
  resourcetype?: any
  components?: string[]
  timezone?: string
  projectedProps?: Record<string, unknown>
  calendarColor?: {
    _cdata: string
  }
  folderType: 'calendar' | 'subscription'
  isDefault: boolean
  isSync: boolean
  isShared: boolean
  isSharedOwner: boolean
  isReadOnly: boolean
  owner?: string
  events: Record<string, CalendarEventObject>
  [key: string]: any
}

/**
 * Calendar event object wrapper
 */
export interface CalendarEventObject {
  url: string
  etag: string
  color: string
  event: VEvent
  folderId: string
  dirty: boolean
}

/**
 * Time range for event fetching
 */
export interface TimeRange {
  start: string
  end: string
}

/**
 * Fetch queue item for managing sync operations
 */
interface FetchQueueItem {
  key: string
  calendar: AppCalendar
  start: string
  end: string
}

/**
 * 2. 캘린더 컬렉션 가져오기
 * Fetches calendar collections from CalDAV server
 */
const fetchCalendars = async (
  isInit: boolean = false,
  completeCallback: (success: boolean) => void = (_success) => {},
): Promise<void> => {
  if (isFetchingCalendars) {
    return
  }

  // CalDAV PROPFIND 요청
  const client = getClient()
  if (client == null) {
    completeCallback(false)
    return
  }

  setIsFetchingCalendars(true)
  _emitSync()

  client
    .fetchCalendars({
      props: {
        [`${DAVNamespaceShort.CALDAV}:calendar-description`]: {},
        [`${DAVNamespaceShort.CALDAV}:calendar-timezone`]: {},
        [`${DAVNamespaceShort.DAV}:displayname`]: {},
        [`${DAVNamespaceShort.CALDAV_APPLE}:calendar-color`]: {},
        [`${DAVNamespaceShort.CALENDAR_SERVER}:getctag`]: {},
        [`${DAVNamespaceShort.DAV}:resourcetype`]: {},
        [`${DAVNamespaceShort.CALDAV}:supported-calendar-component-set`]: {},
        [`${DAVNamespaceShort.DAV}:sync-token`]: {},
        [`${DAVNamespaceShort.CALDAV_APPLE}:calendar-order`]: {},
        [`${DAVNamespaceShort.DAV}:creationdate`]: {},
        [`${DAVNamespaceShort.DAV}:current-user-privilege-set`]: {},
      },
      projectedProps: {
        calendarOrder: true,
        creationdate: true,
        currentUserPrivilegeSet: true,
      },
    })
    .then(async (response: DAVCalendar[]) => {
      // VTODO
      const todoFolders: DAVCalendar[] = []

      // VEVENT 필터링 (VTODO 폴더는 별도 처리)
      const folders = response.filter((calendar) => {
        // VTODO만 포함하고 VEVENT는 포함하지 않는 폴더들을 todoFolders에 추가
        if (calendar.components?.includes('VTODO') && !calendar.components?.includes('VEVENT')) {
          todoFolders.push(calendar)
          return false // 캘린더 폴더에서는 제외
        }
        // VEVENT를 포함하는 폴더만 캘린더 폴더로 처리
        return calendar.components?.includes('VEVENT')
      })

      if (getAppServices().pages.todo === true) {
        // VTODO가 있는지 플래그 추가
        const supportVTodo = response.some((calendar) => calendar.components?.includes('VTODO'))
        // TodoService barrel re-export 순환 참조 방지를 위해 소스 모듈에서 직접 dynamic import
        const todoFolderSync = await import('pages/todo/modules/TodoFolderSync')

        if (todoFolders.length > 0) {
          // todo folder 가 없다면, 만들어야 처리하도록.
          todoFolderSync.setFolders(
            {
              name: userInfo.name,
              email: userInfo.email,
            },
            todoFolders as any,
            isInit,
          )

          // if (hasChanges) {
          //   folderEmitter.emit('folder')
          // }
        } else if (supportVTodo) {
          todoFolderSync.setAccount(userInfo)
          const { initDefaultTodoFolder } = await import('pages/todo/modules/TodoFolderManager')
          initDefaultTodoFolder({
            name: userInfo.name,
            email: userInfo.email,
          })
        }
      }

      // 동기화 설정 관리
      const calendarSettings = settings.getValue('calendar')
      const isFirstFetch = !Array.isArray(calendarSettings.calendarSyncTargets)
      const calendars: AppCalendar[] = []
      let oldCalendars = (classInfo.calendars || []) as AppCalendar[]
      let hasSharedCalendars = false

      folders.forEach((calendar) => {
        //TODO: 설정을 통해 싱크 여부 반영.
        let oldCalendar: AppCalendar | null = null
        let willRemoveIndex = -1
        oldCalendars.some((o, index) => {
          if (o.url === calendar.url) {
            oldCalendar = o as AppCalendar
            willRemoveIndex = index
            return true
          }
          return false
        })
        if (willRemoveIndex > -1) {
          oldCalendars.splice(willRemoveIndex, 1)
        }

        const isNew = oldCalendar === null
        const c = {
          ...calendar,
          folderType: 'calendar' as const,
          isDefault: false,
          isSync: false,
          isShared: false,
          isSharedOwner: false,
          isReadOnly: false,
          events: {},
        } as unknown as AppCalendar
        c.folderType = 'calendar'
        c.isDefault = calendar.url.endsWith('/Default/')
        if (isFirstFetch) {
          c.isSync = c.isDefault
          if (c.isSync) {
            calendarSettings.calendarSyncTargets = [calendar.url]
            settings.setValue('calendar', calendarSettings)
          }
        } else {
          c.isSync = calendarSettings.calendarSyncTargets.includes(calendar.url)
        }
        c.isShared = calendar.resourcetype.includes('shared')
        c.isSharedOwner = calendar.resourcetype.includes('sharedOwner')
        if (!c.calendarColor || !c.calendarColor._cdata || c.calendarColor._cdata === '') {
          const hashString = `${userInfo.email}/${calendar.displayName}`
          c.calendarColor = {
            _cdata: basicColors[Math.abs(stringToHash(hashString)) % basicColors.length],
          }
        }
        if (!c.isShared) {
          c.owner = userInfo.email
        } else {
          hasSharedCalendars = true
          const urlParts = calendar.url.split('/')
          const ownerFolderId = urlParts[urlParts.length - 2]
          const hasEmail = ownerFolderId.includes('@')
          if (hasEmail) {
            const splited = ownerFolderId.split('.')
            const email = splited.slice(0, splited.length - 1).join('.')
            if (isPersonaEmail(email)) {
              c.owner = getDisplayEmailFromPersonaEmail(email)
            } else {
              c.owner = email
            }
          }
        }
        const privilegeSet = calendar.projectedProps?.currentUserPrivilegeSet as any
        c.isReadOnly = !privilegeSet?.privilege?.find((p: any) => !!p['writeContent'])
        if (isNew) {
          c.events = {}
        } else {
          c.events = oldCalendar!.events
        }
        calendars.push(c)
      })

      const locale = settings.getValue('general').locale
      // 캘린더 정렬 (Default 우선)
      calendars.sort((a, b) => {
        if (a.isDefault) return -1
        if (b.isDefault) return 1

        if (a.isShared !== b.isShared) {
          return a.isShared ? 1 : -1
        }

        // if (a.projectedProps.calendarOrder._cdata && b.projectedProps.calendarOrder._cdata) {
        //   return (
        //     parseInt(a.projectedProps.calendarOrder._cdata) -
        //     parseInt(b.projectedProps.calendarOrder._cdata)
        //   )
        // }

        return (a.displayName as string).localeCompare(b.displayName as string, locale)
      })

      // subscription 캘린더 보존 (CalDAV 캘린더만 교체)
      const subscriptionCalendars = (classInfo.calendars || []).filter(
        (c: any) => c.folderType === 'subscription',
      )

      oldCalendars.forEach((o) => {
        if (o.folderType !== 'subscription') {
          _removeInstances(o.events)
          o.events = {}
        }
      })
      classInfo.calendars = [...calendars, ...subscriptionCalendars] as any
      classInfo.hasSharedCalendars = hasSharedCalendars
      folderEmitter.emit('folder')

      if (isInit) {
        reloadEvents()
      }
      completeCallback(true)
      if (classInfo.calendars.length === 0) {
        console.error('fetch calendar is zero! retry on more')
      }
    })
    .catch(function (reason) {
      console.error('fetch calendars failed %o', reason)
      completeCallback(false)
    })
    .finally(() => {
      setIsFetchingCalendars(false)
      _emitSync()
    })
}

/**
 * 3. 이벤트 Fetch 처리
 * 큐 시스템 - 중복 요청 방지
 */
const _addFetchQueue = (folder: AppCalendar, startDate: string, endDate: string): boolean => {
  const key = folder.url + '_' + startDate + '_' + endDate
  const isContains = fetchingEventQueue.some((item) => {
    if (item.key === key) {
      return true
    }
    return false
  })
  if (isContains) {
    console.log('already fetching event : %s', key)
    return false
  }

  const newQueue: FetchQueueItem[] = [
    ...fetchingEventQueue,
    {
      key: key,
      calendar: folder,
      start: startDate,
      end: endDate,
    },
  ]
  setFetchingEventQueue(newQueue)

  if (newQueue.length > 1) {
    return false
  }
  if (newQueue.length === 1) {
    _emitSync()
  }
  return true
}

const _completeFetchQueue = (key: string): void => {
  const newQueue = fetchingEventQueue.filter((item) => {
    if (item.key === key) {
      return false
    }
    return true
  })
  setFetchingEventQueue(newQueue)

  if (newQueue.length > 0) {
    const target = newQueue[0]
    _fetchEvents(target.calendar, target.start, target.end, () => {
      const key = target.calendar.url + '_' + target.start + '_' + target.end
      _completeFetchQueue(key)
    })
  } else {
    _emitSync()
  }
}

const _addFetchAction = (folder: AppCalendar, startDate: string, endDate: string): void => {
  const canFetch = _addFetchQueue(folder, startDate, endDate)
  if (!canFetch) {
    return
  }
  _fetchEvents(folder, startDate, endDate, () => {
    const key = folder.url + '_' + startDate + '_' + endDate
    _completeFetchQueue(key)
  })
}

/**
 * 4. 이벤트 데이터 처리
 * Fetches calendar event objects from CalDAV server
 */
const _fetchEvents = async (
  folder: AppCalendar,
  startDate: string,
  endDate: string,
  completeCallback: () => void = () => {},
): Promise<void> => {
  if (!folder.isSync) {
    completeCallback()
    return
  }

  const events: DAVCalendarObject[] = []
  const client = getClient()
  if (client == null) {
    completeCallback()
    return
  }
  try {
    // CalDAV REPORT 요청
    const eventArr = await client.fetchCalendarObjects({
      calendar: folder as any,
      timeRange: { start: startDate, end: endDate },
    })
    if (eventArr.length > 0) {
      events.push(...eventArr)
    }
  } catch (error) {
    console.log('fetch events failed %o', error)
    completeCallback()
    return
  }

  if (!folder.isSync) {
    // Fetch 중에 동기화 해제 되면 종료.
    completeCallback()
    return
  }

  const folderTimeRange = latestTimeRange[folder.url]
  const isTimeRangeChanged =
    folderTimeRange?.start !== startDate || folderTimeRange?.end !== endDate
  setLatestTimeRange(folder.url, { start: startDate, end: endDate })

  const eventDict: Record<string, CalendarEventObject> = {}
  const allOldEvents = { ...folder.events }

  // etag 기반 변경 감지
  events.forEach((e) => {
    const oldCalObj = allOldEvents[e.url]
    const oldEvent = oldCalObj && oldCalObj.event
    const hasRRULE = oldEvent && oldEvent.rrule !== undefined
    // oldEvent가 없으면 Create. etag가 다르면 Update. etag가 같으면 이벤트 변경 없음.
    if (oldEvent && oldCalObj.etag === e.etag && (!isTimeRangeChanged || !hasRRULE)) {
      // Event 변경 없음.
      eventDict[e.url] = {
        url: e.url,
        etag: e.etag!,
        color: folder.calendarColor!._cdata,
        event: oldEvent,
        folderId: folder.url,
        dirty: false,
      }
      delete allOldEvents[e.url] // instance를 유지하기 위해 제거.
      return
    }
    if (oldEvent && oldCalObj.etag === e.etag) {
      // Instance 재생성.
      eventDict[e.url] = {
        url: e.url,
        etag: e.etag!,
        color: folder.calendarColor!._cdata,
        event: oldEvent,
        folderId: folder.url,
        dirty: true,
      }
    } else {
      // New, Update Event

      // ICS 파싱. node-ical 의 tz parsing 등에서 throw 시 한 이벤트의 잘못된 ICS 가
      // forEach 전체를 죽이지 않도록 try/catch 로 격리하고, 실패 시 해당 이벤트는 skip.
      let vCalendar: CalendarResponse | null = null
      try {
        vCalendar = parseICS(e.data!) as CalendarResponse
      } catch (err) {
        console.warn('parseICS failed, skip event:', e.url, err)
      }

      let masterEvent: VEvent | null = null
      if (vCalendar != null) {
        Object.entries(vCalendar).forEach(([_k, v]) => {
          if (v.type === 'VEVENT') {
            masterEvent = v as VEvent
          }
        })
      }
      if (masterEvent) {
        masterEvent = refineEvent(masterEvent)

        eventDict[e.url] = {
          url: e.url,
          etag: e.etag!,
          color: folder.calendarColor!._cdata,
          event: masterEvent,
          folderId: folder.url,
          dirty: true,
        }
      }
    }
  })

  classInfo.calendars?.some((c: any) => {
    if (c.url === folder.url) {
      c.events = eventDict
      return true
    }
    return false
  })

  // delete,update Event - instance 제거.
  let instances = classInfo.instances
  let hasRemoved = false
  Object.entries(allOldEvents).forEach(([key, _e]) => {
    instances = instances.filter((i) => {
      if (i.serverId === key) {
        hasRemoved = true
        return false
      }
      return true
    })
  })
  classInfo.instances = instances

  // EventEmitter를 통한 상태 변경 알림
  classInfo.instances = _createInstances(eventDict, hasRemoved, classInfo.instances)
  instanceEmitter.emit('instance')

  completeCallback()
}

export const applySync = (folderUrls: string[]) => {
  if (folderUrls == null) return

  classInfo.calendars?.forEach((c: any) => {
    if (c.folderType === 'subscription') return

    const isSync = folderUrls.includes(c.url)
    if (isSync === false && c.isSync === true) {
      _removeInstances(c.events)
      c.events = {}
    }
    c.isSync = isSync
  })
}

/**
 * Enable or disable calendar synchronization
 */
export const enableSync = (folderUrl: string, isSync: boolean, runSync: boolean = true): void => {
  let calendar: AppCalendar | null = null
  classInfo.calendars?.some((c: any) => {
    if (c.url === folderUrl && c.isSync !== isSync) {
      calendar = c as AppCalendar
      c.isSync = isSync
      return true
    }
    return false
  })
  if (calendar === null) {
    return
  }

  const isSubscription = calendar.folderType === 'subscription'

  // subscription 타입은 calendarSyncTargets에 포함시키지 않는다 (CalDAV 전용)
  if (!isSubscription) {
    const calendarSettings = settings.getValue('calendar')
    if (isSync) {
      const newCalendar = {
        ...calendarSettings,
        calendarSyncTargets: [...calendarSettings.calendarSyncTargets, calendar.url],
      }
      settings
        .setValue('calendar', newCalendar)
        .then(() => {
          settings.getStore().dispatch({
            type: 'settings/UPLOAD_SETTINGS',
            payload: { calendar: newCalendar },
          })
        })
        .catch((err) => console.error('Failed to sync calendar settings:', err))
    } else {
      const newCalendar = {
        ...calendarSettings,
        calendarSyncTargets: calendarSettings.calendarSyncTargets.filter(
          (url: string) => url !== calendar!.url,
        ),
      }
      settings
        .setValue('calendar', newCalendar)
        .then(() => {
          settings.getStore().dispatch({
            type: 'settings/UPLOAD_SETTINGS',
            payload: { calendar: newCalendar },
          })
        })
        .catch((err) => console.error('Failed to sync calendar settings:', err))
    }
  }

  if (isSync) {
    if (runSync) {
      if (isSubscription) {
        import('./subscription').then(({ updateSubscription }) => {
          const subscriptionId = folderUrl.replace('subscription://', '').replace(/\/$/, '')
          updateSubscription(subscriptionId, { isSync: true })
        })
      } else {
        const startDate = dateUtils
          .getMonthViewFirstDate(dateUtils.getPrevMonth(currentDate))
          .toISOString()
        const endDate = dateUtils
          .getMonthViewLastDate(dateUtils.getNextMonth(currentDate))
          .toISOString()
        _addFetchAction(calendar, startDate, endDate)
      }
    }
  } else {
    if (isSubscription) {
      import('./subscription').then(({ updateSubscription }) => {
        const subscriptionId = folderUrl.replace('subscription://', '').replace(/\/$/, '')
        updateSubscription(subscriptionId, { isSync: false })
      })
    }
    _removeInstances(calendar.events)
    calendar.events = {}
  }

  folderEmitter.emit('folder')
}

/**
 * 시간 범위 설정
 * Reload calendar events within the current time range
 *
 * @param fullReload true이면 fetchCalendars()로 전체 캘린더 목록을 다시 가져온다.
 * @param skipSync true이면 서버 설정 동기화(settingSyncService.sync)를 건너뛴다.
 *                 서버에서 수신한 설정 변경으로 인한 reload 시 사용하여,
 *                 throttle 시간(1초) 이내의 불필요한 서버 재요청을 방지한다.
 */
export const reloadEvents = (fullReload: boolean = false, skipSync: boolean = false): void => {
  // fullReload이면서 skipSync가 아닌 경우에만 서버 설정을 강제 동기화한다.
  // skipSync=true인 경우는 서버에서 이미 수신한 데이터로 인한 reload이므로 재요청 불필요.
  if (skipSync === false) {
    settingSyncService.sync(fullReload).catch(() => {})
  }

  if (fullReload) {
    fetchCalendars()
  }

  const startDate = dateUtils
    .getMonthViewFirstDate(dateUtils.getPrevMonth(currentDate))
    .toISOString()
  const endDate = dateUtils.getMonthViewLastDate(dateUtils.getNextMonth(currentDate)).toISOString()
  classInfo.calendars?.forEach((c: any) => {
    if (c.isSync && c.folderType !== 'subscription') {
      _addFetchAction(c as AppCalendar, startDate, endDate)
    }
  })

  // 구독 캘린더 갱신 (dynamic import로 순환 참조 방지)
  import('./subscription').then(({ reloadSubscriptionInstances }) => {
    reloadSubscriptionInstances()
  })

  fetchNotifications()
}

export { fetchCalendars }
